import React from 'react';
import type { ShorthandValue } from '@archipro-design/aria';
import {
    DropdownProduct,
    Grid,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './OptionPicker.style';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantDecision } from '@modules/product/hook/use-variant-decision';
import { SortDown } from '@archipro-design/icons';
import type AttributeOption from '@modules/product/type/attribute-option';
import type Variant from '@modules/product/type/variant';
import OptionLabelWithTooltip from './OptionLabelWithTooltip';
import type { ButtonProps } from '@fluentui/react-northstar';
import { useTracker } from '@archipro-website/tracker';

export interface BasePickerProps {
    data: {
        variants: Variant[];
    };
    isGeneric?: boolean;
}

type OptionPickerProps = BasePickerProps & {
    data: {
        attributeID: number;
        label: string;
        options: AttributeOption[];
    };
};

interface DropdownItemType {
    header: string;
    key: number;
}

interface DropdownItemType {
    header: string;
    key: number;
}

const emptyOption = {
    key: 0,
    header: 'Not Applicable',
};

const OptionPicker = ({
    data: { attributeID, label, variants, options },
    isGeneric,
}: OptionPickerProps) => {
    const { decision } = useVariantOptionState();
    const { pickOption } = useVariantDecision(variants);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const { css } = useStyles<{ menuOpen: boolean }>({ menuOpen });
    const tracker = useTracker();

    const selectedOptionID = decision.options[attributeID] ?? 0;
    const items = options.map((opt) => ({
        header: opt.Label,
        key: opt.ID,
    }));

    const option =
        items.find((opt) => opt.key === selectedOptionID) ?? emptyOption;

    if (!isGeneric) {
        items.unshift(emptyOption);
    }

    const onOptionChange = (value: ShorthandValue<DropdownItemType>) => {
        const key = value ? (value as DropdownItemType).key : 0;
        tracker.log('DropDownSelect', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: key,
            },
        });
        pickOption({
            ID: key,
            attributeID: attributeID,
        });
    };

    return (
        <Grid columns={`${pxToRem(394)} 1fr`} className={css(S.OptionPicker)}>
            <label className={css(S.OptionLabel)}>{label}</label>
            {items.length > 1 ? (
                <DropdownProduct
                    className={css(S.OptionDropdown)}
                    value={option}
                    placeholder={emptyOption.header}
                    items={items}
                    onChange={(_, { value }) =>
                        onOptionChange(value as DropdownItemType)
                    }
                    toggleIndicator={<SortDown />}
                    onOpenChange={(_, { open }) => setMenuOpen(Boolean(open))}
                    triggerButton={
                        {
                            children: (
                                TriggerButton: React.ElementType<ButtonProps>,
                                props: ButtonProps,
                            ) => {
                                return (
                                    <TriggerButton
                                        {...props}
                                        content={undefined}
                                    >
                                        <OptionLabelWithTooltip
                                            label={option.header}
                                        />
                                    </TriggerButton>
                                );
                            },
                        } as ShorthandValue<ButtonProps>
                    }
                    variables={{
                        belowListBorderRadius: 'unset',
                    }}
                />
            ) : (
                <div className={css(S.SingleValue)}>
                    <OptionLabelWithTooltip label={option.header} />
                </div>
            )}
        </Grid>
    );
};

export default OptionPicker;
